import type { FirmData, PracticeArea, Office, Article, LeadershipProfile, FirmStatistics } from '../types';
import { validateFirmData, type ValidationResult } from '../types/validation';

// Import JSON data
import firmInfoData from '../data/firm-info.json';
import statisticsData from '../data/statistics.json';
import officesData from '../data/offices.json';
import practiceAreasData from '../data/practice-areas.json';
import leadershipData from '../data/leadership.json';
import articlesData from '../data/articles.json';
import approachData from '../data/approach.json';

// Type for approach steps
export interface ApproachStep {
  id: string;
  title: string;
  description: string;
  icon: string;
  details: string[];
}

// Content loading functions
export const loadFirmInfo = (): typeof firmInfoData => {
  return firmInfoData;
};

export const loadStatistics = (): FirmStatistics => {
  return statisticsData as FirmStatistics;
};

export const loadOffices = (): Office[] => {
  return officesData as Office[];
};

export const loadPracticeAreas = (): PracticeArea[] => {
  return practiceAreasData as PracticeArea[];
};

export const loadLeadership = (): LeadershipProfile[] => {
  return leadershipData as LeadershipProfile[];
};

export const loadArticles = (): Article[] => {
  return articlesData as Article[];
};

export const loadApproach = (): ApproachStep[] => {
  return approachData as ApproachStep[];
};

// Comprehensive firm data loader
export const loadFirmData = (): { data: FirmData; validation: ValidationResult } => {
  const firmInfo = loadFirmInfo();
  const statistics = loadStatistics();
  const offices = loadOffices();
  const practiceAreas = loadPracticeAreas();
  const leadership = loadLeadership();
  const insights = loadArticles();

  const firmData: FirmData = {
    name: firmInfo.name,
    tagline: firmInfo.tagline,
    foundingYear: firmInfo.foundingYear,
    statistics,
    offices,
    practiceAreas,
    leadership,
    insights
  };

  const validation = validateFirmData(firmData);

  return {
    data: firmData,
    validation
  };
};

// Content filtering and search utilities
export const filterArticlesByCategory = (articles: Article[], category: string): Article[] => {
  return articles.filter(article => 
    article.category.toLowerCase() === category.toLowerCase()
  );
};

export const getFeaturedArticle = (articles: Article[]): Article | undefined => {
  return articles.find(article => 
    'featured' in article && (article as any).featured === true
  ) || articles[0];
};

export const getRecentArticles = (articles: Article[], limit: number = 5): Article[] => {
  return articles
    .sort((a, b) => new Date(b.publishDate).getTime() - new Date(a.publishDate).getTime())
    .slice(0, limit);
};

export const searchArticles = (articles: Article[], query: string): Article[] => {
  const lowercaseQuery = query.toLowerCase();
  return articles.filter(article =>
    article.title.toLowerCase().includes(lowercaseQuery) ||
    article.excerpt.toLowerCase().includes(lowercaseQuery) ||
    article.category.toLowerCase().includes(lowercaseQuery) ||
    ('tags' in article && Array.isArray((article as any).tags) && 
     (article as any).tags.some((tag: string) => tag.toLowerCase().includes(lowercaseQuery)))
  );
};

export const getArticlesByTag = (articles: Article[], tag: string): Article[] => {
  return articles.filter(article =>
    'tags' in article && Array.isArray((article as any).tags) &&
    (article as any).tags.some((articleTag: string) => 
      articleTag.toLowerCase() === tag.toLowerCase()
    )
  );
};

export const getPracticeAreaByCategory = (practiceAreas: PracticeArea[], category: string): PracticeArea | undefined => {
  return practiceAreas.find(area => 
    area.category.toLowerCase() === category.toLowerCase()
  );
};

export const getOfficesByType = (offices: Office[], type: 'head' | 'regional'): Office[] => {
  return offices.filter(office => office.type === type);
};

export const getHeadOffice = (offices: Office[]): Office | undefined => {
  return offices.find(office => office.type === 'head');
};

// Content validation utilities
export const validateContentIntegrity = (): {
  isValid: boolean;
  errors: string[];
  warnings: string[];
} => {
  const errors: string[] = [];
  const warnings: string[] = [];

  try {
    // Validate firm data
    const { validation } = loadFirmData();
    if (!validation.isValid) {
      errors.push(...validation.errors);
    }

    // Check for required content
    const articles = loadArticles();
    if (articles.length === 0) {
      warnings.push('No articles found in content data');
    }

    const practiceAreas = loadPracticeAreas();
    if (practiceAreas.length === 0) {
      errors.push('No practice areas found - this is required content');
    }

    const offices = loadOffices();
    if (offices.length === 0) {
      errors.push('No offices found - at least one office is required');
    }

    const leadership = loadLeadership();
    if (leadership.length === 0) {
      warnings.push('No leadership profiles found');
    }

    // Check for head office
    const headOffice = getHeadOffice(offices);
    if (!headOffice) {
      errors.push('No head office found - at least one office must be marked as "head"');
    }

  } catch (error) {
    errors.push(`Content loading error: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings
  };
};

// Content update utilities for maintenance
export const getContentSummary = () => {
  const firmInfo = loadFirmInfo();
  const statistics = loadStatistics();
  const offices = loadOffices();
  const practiceAreas = loadPracticeAreas();
  const leadership = loadLeadership();
  const articles = loadArticles();
  const approach = loadApproach();

  return {
    firmName: firmInfo.name,
    lastUpdated: new Date().toISOString(),
    contentCounts: {
      offices: offices.length,
      practiceAreas: practiceAreas.length,
      leadership: leadership.length,
      articles: articles.length,
      approachSteps: approach.length
    },
    statistics: {
      yearsOfPractice: statistics.yearsOfPractice,
      mattersHandled: statistics.mattersHandled,
      clientEngagements: statistics.clientEngagements
    }
  };
};